/**
 * Capacitor BLE Bridge
 *
 * Abstracts BLE operations so they work in both:
 * - Native Capacitor context (iOS/Android) via @capacitor-community/bluetooth-le
 * - Web browser context via Web Bluetooth API
 *
 * All three BLE consumers (bluetooth/mesh, meshtastic, meshcore) should use
 * this bridge instead of accessing navigator.bluetooth directly.
 */

import { Capacitor } from '@capacitor/core'
import { BleClient } from '@capacitor-community/bluetooth-le'
import type { BleDevice, RequestBleDeviceOptions as CapBleDeviceOptions } from '@capacitor-community/bluetooth-le'

// ============= Platform Detection =============

/** Whether we're running inside a Capacitor native shell */
export function isNativeContext(): boolean {
  return Capacitor.isNativePlatform()
}

/** Whether any BLE API is available (native or web) */
export function isBLEAvailable(): boolean {
  return isNativeContext() || 'bluetooth' in navigator
}

// ============= Types =============

/**
 * Unified device handle returned by the bridge.
 * Consumers store this and pass it back for read/write/notify operations.
 */
export interface BridgedBleDevice {
  deviceId: string
  name?: string
  /** Web-only: the raw BluetoothDevice */
  _webDevice?: BluetoothDevice
  /** Web-only: the GATT server */
  _webServer?: BluetoothRemoteGATTServer
}

export interface BleRequestDeviceOptions {
  services?: string[]
  optionalServices?: string[]
  name?: string
  namePrefix?: string
}

export type DisconnectCallback = (deviceId: string) => void
export type NotifyCallback = (value: DataView) => void

// ============= Initialization =============

let _initialized = false

/**
 * Initialize the BLE subsystem. Must be called once before any other bridge call.
 * No-op on web (Web Bluetooth doesn't need initialization).
 */
export async function initBLE(): Promise<void> {
  if (_initialized) return

  if (isNativeContext()) {
    await BleClient.initialize({
      androidNeverForLocation: true,
    })
  }

  _initialized = true
  console.log(`[BLE Bridge] Initialized (native=${isNativeContext()})`)
}

// ============= Availability =============

/**
 * Check if Bluetooth is currently enabled on the device.
 * Web always returns true (permission prompt happens at requestDevice time).
 */
export async function isBLEEnabled(): Promise<boolean> {
  if (isNativeContext()) {
    return BleClient.isEnabled()
  }
  // Web: use getAvailability if available
  if ('bluetooth' in navigator) {
    try {
      return await navigator.bluetooth.getAvailability()
    } catch {
      return true // assume available; user will get prompt
    }
  }
  return false
}

// ============= Device Discovery =============

/**
 * Request a device using a picker dialog (both platforms).
 */
export async function requestDevice(
  options: BleRequestDeviceOptions
): Promise<BridgedBleDevice> {
  await initBLE()

  if (isNativeContext()) {
    const capOptions: CapBleDeviceOptions = {
      services: options.services,
      optionalServices: options.optionalServices,
      name: options.name,
      namePrefix: options.namePrefix,
    }

    const device: BleDevice = await BleClient.requestDevice(capOptions)
    return {
      deviceId: device.deviceId,
      name: device.name,
    }
  }

  // Web Bluetooth path
  const webOptions: RequestDeviceOptions = {}
  if (options.services?.length) {
    webOptions.filters = [{ services: options.services }]
  }
  if (options.optionalServices?.length) {
    webOptions.optionalServices = options.optionalServices
  }
  if (!webOptions.filters && !webOptions.optionalServices) {
    webOptions.acceptAllDevices = true
  }

  const device = await navigator.bluetooth.requestDevice(webOptions)
  return {
    deviceId: device.id,
    name: device.name ?? undefined,
    _webDevice: device,
  }
}

// ============= Connection =============

/**
 * Connect to a previously-discovered device.
 */
export async function connect(
  device: BridgedBleDevice,
  onDisconnect?: DisconnectCallback
): Promise<void> {
  if (isNativeContext()) {
    await BleClient.connect(device.deviceId, onDisconnect ? () => onDisconnect(device.deviceId) : undefined)
    return
  }

  // Web: connect via GATT
  const webDevice = device._webDevice
  if (!webDevice?.gatt) {
    throw new Error('[BLE Bridge] No web device/gatt available for connect')
  }

  const server = await webDevice.gatt.connect()
  device._webServer = server

  if (onDisconnect) {
    webDevice.addEventListener('gattserverdisconnected', () => {
      device._webServer = undefined
      onDisconnect(device.deviceId)
    })
  }
}

/**
 * Disconnect from a device.
 */
export async function disconnect(device: BridgedBleDevice): Promise<void> {
  if (isNativeContext()) {
    await BleClient.disconnect(device.deviceId)
    return
  }

  if (device._webServer?.connected) {
    device._webServer.disconnect()
  }
  device._webServer = undefined
}

// ============= Read / Write =============

/**
 * Read a characteristic value.
 */
export async function read(
  device: BridgedBleDevice,
  serviceUUID: string,
  characteristicUUID: string
): Promise<DataView> {
  if (isNativeContext()) {
    return BleClient.read(device.deviceId, serviceUUID, characteristicUUID)
  }

  const server = device._webServer
  if (!server) throw new Error('[BLE Bridge] Not connected (web)')
  const service = await server.getPrimaryService(serviceUUID)
  const char = await service.getCharacteristic(characteristicUUID)
  return char.readValue()
}

/**
 * Write a value to a characteristic (with response).
 */
export async function write(
  device: BridgedBleDevice,
  serviceUUID: string,
  characteristicUUID: string,
  value: DataView
): Promise<void> {
  if (isNativeContext()) {
    await BleClient.write(device.deviceId, serviceUUID, characteristicUUID, value)
    return
  }

  const server = device._webServer
  if (!server) throw new Error('[BLE Bridge] Not connected (web)')
  const service = await server.getPrimaryService(serviceUUID)
  const char = await service.getCharacteristic(characteristicUUID)
  await char.writeValue((value.buffer as ArrayBuffer).slice(value.byteOffset, value.byteOffset + value.byteLength))
}

/**
 * Write a value to a characteristic (without response / fire-and-forget).
 */
export async function writeWithoutResponse(
  device: BridgedBleDevice,
  serviceUUID: string,
  characteristicUUID: string,
  value: DataView
): Promise<void> {
  if (isNativeContext()) {
    await BleClient.writeWithoutResponse(device.deviceId, serviceUUID, characteristicUUID, value)
    return
  }

  const server = device._webServer
  if (!server) throw new Error('[BLE Bridge] Not connected (web)')
  const service = await server.getPrimaryService(serviceUUID)
  const char = await service.getCharacteristic(characteristicUUID)
  await char.writeValue((value.buffer as ArrayBuffer).slice(value.byteOffset, value.byteOffset + value.byteLength))
}

// ============= Notifications =============

/**
 * Start receiving notifications for a characteristic.
 * Returns a cleanup function to stop notifications.
 */
export async function startNotifications(
  device: BridgedBleDevice,
  serviceUUID: string,
  characteristicUUID: string,
  callback: NotifyCallback
): Promise<() => Promise<void>> {
  if (isNativeContext()) {
    await BleClient.startNotifications(
      device.deviceId,
      serviceUUID,
      characteristicUUID,
      callback
    )
    return async () => {
      await BleClient.stopNotifications(device.deviceId, serviceUUID, characteristicUUID)
    }
  }

  // Web path
  const server = device._webServer
  if (!server) throw new Error('[BLE Bridge] Not connected (web)')
  const service = await server.getPrimaryService(serviceUUID)
  const char = await service.getCharacteristic(characteristicUUID)
  await char.startNotifications()

  const handler = (event: Event) => {
    const target = event.target as BluetoothRemoteGATTCharacteristic
    if (target.value) {
      callback(target.value)
    }
  }

  char.addEventListener('characteristicvaluechanged', handler)

  return async () => {
    char.removeEventListener('characteristicvaluechanged', handler)
    try {
      await char.stopNotifications()
    } catch {
      // May fail if already disconnected
    }
  }
}

// ============= Helpers =============

/**
 * Convert a Uint8Array to a DataView (convenience for callers that have raw bytes).
 */
export function uint8ToDataView(data: Uint8Array): DataView {
  return new DataView(data.buffer, data.byteOffset, data.byteLength)
}

/**
 * Convert a DataView to a Uint8Array.
 */
export function dataViewToUint8(view: DataView): Uint8Array {
  return new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
}
